// Initialize flag to track if GA is loaded
let isInitialized = false;

/**
 * Initializes Google Analytics
 * @returns {boolean} True if initialization was successful
 */
export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return false;
  if (isInitialized) return true;
  if (!process.env.REACT_APP_GA_MEASUREMENT_ID) {
    console.warn('Google Analytics measurement ID not provided');
    return false;
  }

  try {
    // Load the Google Analytics script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_MEASUREMENT_ID}`;
    script.async = true;
    script.onerror = () => {
      console.error('Failed to load Google Analytics script');
    };
    document.head.appendChild(script);

    // Initialize the dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configure GA
    window.gtag('js', new Date());
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
      send_page_view: false // We'll handle page views manually
    });

    isInitialized = true;
    return true;
  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
    return false;
  }
};

/**
 * Tracks a page view
 * @param {string} path - The page path to track
 */
export const trackPageView = (path) => {
  if (!isInitialized || typeof window === 'undefined' || !window.gtag) {
    console.warn('Analytics not initialized - page view not tracked');
    return;
  }

  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      send_to: process.env.REACT_APP_GA_MEASUREMENT_ID
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Tracks a custom event
 * @param {Object} params - Event parameters
 * @param {string} params.action - The event action
 * @param {string} [params.category] - The event category
 * @param {string} [params.label] - The event label
 * @param {number} [params.value] - The event value
 */
export const trackEvent = ({ action, category, label, value }) => {
  if (!isInitialized || typeof window === 'undefined' || !window.gtag) {
    console.warn('Analytics not initialized - event not tracked');
    return;
  }

  try {
    const eventParams = {
      send_to: process.env.REACT_APP_GA_MEASUREMENT_ID
    };

    if (category) eventParams.event_category = category;
    if (label) eventParams.event_label = label;
    if (value) eventParams.value = value;

    window.gtag('event', action, eventParams);
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Tracks an exception
 * @param {string} description - Error description
 * @param {boolean} [fatal=false] - Whether the error was fatal
 */
export const trackError = (description, fatal = false) => {
  if (!isInitialized || typeof window === 'undefined' || !window.gtag) return;

  try {
    window.gtag('event', 'exception', {
      description,
      fatal,
      send_to: process.env.REACT_APP_GA_MEASUREMENT_ID
    });
  } catch (error) {
    console.error('Error tracking exception:', error);
  }
};

/**
 * Sets GA user properties
 * @param {Object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (!isInitialized || typeof window === 'undefined' || !window.gtag) return;

  try {
    window.gtag('set', 'user_properties', properties);
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};